import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Grid,
} from '@mui/material';
import { Layout } from '../components/Layout';
import { config } from '../config';
import { useDeveloperInfo } from '../hooks/useApi';
import { getQuotaUrl, getEndpointSlug } from '../utils/docs';
import type { ApiEndpoint } from '../types';
import type { QuotaResponse } from '../types';

type QuotaState = QuotaResponse | 'loading' | 'error';

export function UsagePage() {
  const { data, isLoading: infoLoading, error: infoError } = useDeveloperInfo();
  const [quotaBySlug, setQuotaBySlug] = useState<Record<string, QuotaState>>({});

  const endpoints = (data?.endpoints ?? []) as ApiEndpoint[];
  const apiKey = data?.user.apiKey ?? '';

  const endpointKey = endpoints.map((ep) => ep.docsUrl ?? ep.documentation_url ?? '').join(',');

  useEffect(() => {
    if (!apiKey || endpoints.length === 0) return;

    const slugs = endpoints.map((ep) =>
      getEndpointSlug(ep.docsUrl ?? ep.documentation_url ?? '')
    );
    slugs.forEach((slug) => setQuotaBySlug((prev) => ({ ...prev, [slug]: 'loading' })));

    endpoints.forEach((endpoint) => {
      const slug = getEndpointSlug(endpoint.docsUrl ?? endpoint.documentation_url ?? '');
      const quotaUrl = getQuotaUrl(
        endpoint.docsUrl ?? endpoint.documentation_url ?? '',
        config.apiUrl
      );
      if (!quotaUrl) {
        setQuotaBySlug((prev) => ({ ...prev, [slug]: 'error' }));
        return;
      }
      fetch(quotaUrl, { headers: { 'x-api-key': apiKey } })
        .then((res) => {
          if (!res.ok) throw new Error('Quota not available');
          return res.json();
        })
        .then((json: QuotaResponse) => {
          setQuotaBySlug((prev) => ({ ...prev, [slug]: json }));
        })
        .catch(() => {
          setQuotaBySlug((prev) => ({ ...prev, [slug]: 'error' }));
        });
    });
  }, [apiKey, endpointKey]);

  if (infoLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (infoError) {
    return (
      <Layout>
        <Alert severity="error">
          Failed to load developer information. Please try again later.
        </Alert>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <Alert severity="warning">No data available.</Alert>
      </Layout>
    );
  }

  if (endpoints.length === 0) {
    return (
      <Layout>
        <Alert severity="info">No endpoints are currently available for your account.</Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h5" gutterBottom>
        Usage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Quota usage per endpoint. Endpoints that do not expose a quota show &quot;Quota not available&quot;.
      </Typography>
      <Grid container spacing={3}>
        {endpoints.map((endpoint, index) => {
          const slug = getEndpointSlug(endpoint.docsUrl ?? endpoint.documentation_url ?? '');
          const quota = quotaBySlug[slug];
          return (
            <Grid item xs={12} md={6} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    fontFamily="monospace"
                    gutterBottom
                  >
                    {endpoint.prefix}
                  </Typography>
                  {endpoint.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {endpoint.description}
                    </Typography>
                  )}
                  {quota === 'loading' && (
                    <Box display="flex" alignItems="center" gap={1} py={1}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading quota…
                      </Typography>
                    </Box>
                  )}
                  {quota === 'error' && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Quota not available
                    </Alert>
                  )}
                  {quota && quota !== 'loading' && quota !== 'error' && (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Used:</strong> {quota.quota_used} / {quota.quota_max}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Remaining:</strong> {quota.quota_remaining}
                      </Typography>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Layout>
  );
}
